// TaskItems.jsx - Unified component for subtasks and checklist items
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  TextField,
  Typography,
  Checkbox,
  LinearProgress,
  Fade,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
// Note: Drag and drop functionality removed for now - can be added back with a library

// Import stores
import useTaskStore from '../../../stores/taskStore';

/**
 * TaskItems - Component for managing checklist items
 * Features inline editing and progress tracking
 * Note: Subtasks are handled differently as they are separate task references
 */
const TaskItems = ({
  taskId,
  type = 'checklist', // Currently only supports 'checklist'
  sx = {},
}) => {
  const theme = useTheme();
  const inputRef = useRef(null);
  const [items, setItems] = useState([]);
  const [newItemText, setNewItemText] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState('');
  
  // Task store
  const { currentTask, patchTask, fetchTask } = useTaskStore();
  
  // Load items from current task
  useEffect(() => {
    console.log('🚀 ~ TaskItems ~ useEffect ~ currentTask changed:', currentTask?._id);
    console.log('🚀 ~ TaskItems ~ useEffect ~ currentTask checklist:', currentTask?.checklist);
    
    if (currentTask) {
      if (type === 'checklist') {
        // Checklist items are embedded in the task
        const checklistItems = (currentTask.checklist || []).map(item => ({
          id: item._id,
          text: item.text,
          completed: item.isCompleted
        }));
        console.log('🚀 ~ TaskItems ~ useEffect ~ Setting items:', checklistItems);
        setItems(checklistItems);
      } else {
        // Subtasks would need to be loaded separately as they are references
        // For now, we'll just show an empty list for subtasks
        setItems([]);
      }
    } else {
      // Clear items when no task is selected
      console.log('🚀 ~ TaskItems ~ useEffect ~ Clearing items (no currentTask)');
      setItems([]);
    }
  }, [currentTask, type, currentTask?.checklist]);
  
  // Focus input when adding
  useEffect(() => {
    if (isAdding && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isAdding]);
  
  // Calculate progress
  const completedCount = items.filter(item => item.completed).length;
  const totalCount = items.length;
  const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  
  // Handle reordering (placeholder for future drag-and-drop implementation)
  const handleReorder = async (fromIndex, toIndex) => {
    const newItems = Array.from(items);
    const [reorderedItem] = newItems.splice(fromIndex, 1);
    newItems.splice(toIndex, 0, reorderedItem);
    
    setItems(newItems);
    
    if (type === 'checklist') {
      // For reordering, we'll send the entire reordered array
      // The backend will handle the reordering logic
      await patchTask(taskId, { 
        checklist: { reorder: newItems.map(item => item.id) }
      });
    }
    // TODO: Handle subtask reordering
  };
  
  // Add new item
  const handleAddItem = async () => {
    if (!newItemText.trim() || !currentTask) return;
    
    if (type === 'checklist') {
      console.log('🚀 ~ TaskItems ~ handleAddItem ~ Adding item:', newItemText.trim());
      try {
        const result = await patchTask(taskId, {
          checklist: { add: { text: newItemText.trim(), isCompleted: false } }
        });
        
        console.log('🚀 ~ TaskItems ~ handleAddItem ~ Result:', result);
        
        // If the result is successful, the currentTask should be updated automatically
        // by the patchTask method. The useEffect will handle updating the local items state.
        if (result) {
          setNewItemText('');
          setIsAdding(false);
        }
      } catch (error) {
        console.error('🚀 ~ TaskItems ~ handleAddItem ~ Failed to add checklist item:', error);
      }
    }
    // TODO: Handle subtasks separately
  };
  
  // Toggle item completion
  const handleToggleItem = async (itemId) => {
    if (!currentTask) return;
    
    if (type === 'checklist') {
      console.log('🚀 ~ TaskItems ~ handleToggleItem ~ Toggling item:', itemId);
      try {
        const result = await patchTask(taskId, {
          checklist: { toggle: itemId }
        });
        console.log('🚀 ~ TaskItems ~ handleToggleItem ~ Result:', result);
        // The currentTask will be updated automatically by patchTask
      } catch (error) {
        console.error('🚀 ~ TaskItems ~ handleToggleItem ~ Failed to toggle checklist item:', error);
      }
    }
    // TODO: Handle subtasks separately
  };
  
  // Update item text
  const handleUpdateItem = async (itemId, newText) => {
    if (!newText.trim()) {
      // If empty, delete the item
      handleDeleteItem(itemId);
      return;
    }
    
    if (!currentTask) return;
    
    if (type === 'checklist') {
      try {
        const result = await patchTask(taskId, {
          checklist: { update: { id: itemId, text: newText.trim() } }
        });
        
        if (result) {
          setEditingId(null);
          setEditingText('');
        }
      } catch (error) {
        console.error('Failed to update checklist item:', error);
      }
    }
    // TODO: Handle subtasks separately
  };
  
  // Delete item
  const handleDeleteItem = async (itemId) => {
    if (!currentTask) return;
    
    if (type === 'checklist') {
      try {
        const result = await patchTask(taskId, {
          checklist: { remove: itemId }
        });
        // The currentTask will be updated automatically by patchTask
      } catch (error) {
        console.error('Failed to delete checklist item:', error);
      }
    }
    // TODO: Handle subtasks separately
  };
  
  // Start editing
  const startEditing = (item) => {
    setEditingId(item.id);
    setEditingText(item.text);
  };
  
  // Cancel editing
  const cancelEditing = () => {
    setEditingId(null);
    setEditingText('');
  };
  
  // Handle key press
  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      action();
    } else if (e.key === 'Escape') {
      if (isAdding) {
        setIsAdding(false);
        setNewItemText('');
      } else if (editingId) {
        cancelEditing();
      }
    }
  };
  
  return (
    <Box sx={{
      ...sx,
      p: 1.5,
      bgcolor: alpha(theme.palette.background.paper, 0.5),
      borderRadius: 1,
      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    }}>
      {/* Compact progress indicator */}
      {totalCount > 0 && (
        <Box sx={{ 
          mb: 1.5,
          pb: 1.5,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
            <Typography 
              variant="caption" 
              sx={{ 
                fontWeight: 600,
                color: theme.palette.text.secondary,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                fontSize: '0.7rem'
              }}
            >
              Progress
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                fontWeight: 600,
                color: progress === 100 ? theme.palette.success.main : theme.palette.text.secondary,
              }}
            >
              {completedCount}/{totalCount}
              {progress === 100 && (
                <CheckIcon sx={{ fontSize: 12, ml: 0.5, verticalAlign: 'middle', color: theme.palette.success.main }} />
              )}
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            sx={{ 
              height: 4,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.divider, 0.08),
              '& .MuiLinearProgress-bar': {
                borderRadius: 2,
                bgcolor: progress === 100 
                  ? theme.palette.success.main
                  : theme.palette.primary.main,
              }
            }}
          />
        </Box>
      )}
      
      {/* Items list */}
      {items.length > 0 && (
        <List sx={{ p: 0, mb: 1 }} dense>
        {items.map((item, index) => (
          <ListItem
            key={item.id}
            sx={{
              pl: 1,
              pr: 1,
              py: 0.75,
              mb: 0.25,
              bgcolor: item.completed 
                ? alpha(theme.palette.action.hover, 0.02)
                : theme.palette.background.paper,
              borderRadius: 0.5,
              border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              transition: 'all 0.15s ease',
              position: 'relative',
              '&:hover': {
                bgcolor: alpha(theme.palette.action.hover, 0.08),
                borderColor: alpha(theme.palette.divider, 0.15),
                '& .delete-button': {
                  opacity: 1,
                },
              },
              ...(item.completed && {
                opacity: 0.6,
                '&:hover': {
                  opacity: 0.8,
                }
              })
            }}
          >
            {/* Checkbox */}
            <ListItemIcon sx={{ minWidth: 36 }}>
              <Checkbox
                edge="start"
                checked={item.completed}
                onChange={() => handleToggleItem(item.id)}
                size="small"
                sx={{
                  color: theme.palette.action.active,
                  '&.Mui-checked': {
                    color: theme.palette.success.main,
                  },
                  '& .MuiSvgIcon-root': {
                    fontSize: 20,
                  },
                  transition: 'transform 0.2s ease',
                  '&:hover': {
                    transform: 'scale(1.1)',
                  }
                }}
              />
            </ListItemIcon>
            
            {/* Text */}
            {editingId === item.id ? (
              <Box sx={{ flex: 1, display: 'flex', gap: 0.5 }}>
                <TextField
                  value={editingText}
                  onChange={(e) => setEditingText(e.target.value)}
                  onKeyDown={(e) => handleKeyPress(e, () => handleUpdateItem(item.id, editingText))}
                  size="small"
                  fullWidth
                  autoFocus
                  variant="outlined"
                  sx={{ 
                    margin: 0, // Fix the margin-bottom issue
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      fontSize: '0.875rem',
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      py: 0.5,
                      px: 1,
                      fontSize: '0.875rem',
                    }
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => handleUpdateItem(item.id, editingText)}
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={cancelEditing}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <ListItemText
                primary={item.text}
                onClick={() => startEditing(item)}
                sx={{
                  cursor: 'pointer',
                  textDecoration: item.completed ? 'line-through' : 'none',
                  color: item.completed ? 'text.secondary' : 'text.primary',
                  '& .MuiListItemText-primary': {
                    fontSize: '0.875rem',
                    fontWeight: item.completed ? 400 : 500,
                    transition: 'all 0.2s ease',
                    ...(item.completed && {
                      textDecorationColor: alpha(theme.palette.text.secondary, 0.4),
                      textDecorationThickness: '1px',
                    })
                  },
                  '&:hover .MuiListItemText-primary': {
                    color: theme.palette.primary.main,
                  }
                }}
              />
            )}
            
            {/* Delete button */}
            {editingId !== item.id && (
              <Fade in>
                <IconButton
                  className="delete-button"
                  size="small"
                  onClick={() => handleDeleteItem(item.id)}
                  sx={{
                    opacity: 0,
                    transition: 'opacity 0.2s',
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Fade>
            )}
          </ListItem>
        ))}
        </List>
      )}
      
      {/* Add new item */}
      {isAdding ? (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 0.5, 
          mt: items.length > 0 ? 0.5 : 0,
          p: 1,
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          borderRadius: 0.5,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
        }}>
          <TextField
            ref={inputRef}
            value={newItemText}
            onChange={(e) => setNewItemText(e.target.value)}
            onKeyDown={(e) => handleKeyPress(e, handleAddItem)}
            placeholder="Add checklist item"
            size="small"
            fullWidth
            variant="outlined"
            sx={{ 
              margin: 0, // Fix the margin-bottom issue
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                '& fieldset': {
                  borderColor: theme.palette.divider,
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
              '& .MuiOutlinedInput-input': {
                py: 1,
                px: 1.5,
              }
            }}
          />
          <IconButton size="small" onClick={handleAddItem}>
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => {
            setIsAdding(false);
            setNewItemText('');
          }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      ) : (
        <Box
          onClick={() => setIsAdding(true)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            py: 0.75,
            mt: items.length > 0 ? 0.5 : 0,
            borderRadius: 0.5,
            cursor: 'pointer',
            border: `1px dashed ${alpha(theme.palette.divider, 0.3)}`,
            color: theme.palette.text.secondary,
            transition: 'all 0.15s ease',
            '&:hover': {
              borderColor: theme.palette.primary.main,
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              color: theme.palette.primary.main,
            },
          }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
          <Typography variant="caption" sx={{ fontWeight: 500 }}>
            Add item
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default TaskItems;